import type { KaiConversation } from "./kaiTypes";

export type ConversationHistoryPeriod =
  | "today"
  | "yesterday"
  | "last_week"
  | "last_month"
  | "older";

export const CONVERSATION_HISTORY_PERIOD_ORDER: ConversationHistoryPeriod[] = [
  "today",
  "yesterday",
  "last_week",
  "last_month",
  "older",
];

export const CONVERSATION_HISTORY_PERIOD_LABELS: Record<
  ConversationHistoryPeriod,
  string
> = {
  today: "Today",
  yesterday: "Yesterday",
  last_week: "Last week",
  last_month: "Last month",
  older: "Older",
};

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Calendar days since the conversation started (local timezone). */
export function conversationAgeDays(createdAt: string, now = new Date()): number {
  const created = startOfLocalDay(new Date(createdAt));
  const today = startOfLocalDay(now);
  const diffMs = today.getTime() - created.getTime();
  return Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)));
}

export function conversationStartDate(conversation: KaiConversation): string {
  if (conversation.createdAt) return conversation.createdAt;
  const firstMessage = conversation.messages[0];
  if (firstMessage?.createdAt) return firstMessage.createdAt;
  return conversation.updatedAt;
}

export function conversationPeriod(
  createdAt: string,
  now = new Date(),
): ConversationHistoryPeriod {
  const days = conversationAgeDays(createdAt, now);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days <= 7) return "last_week";
  if (days <= 30) return "last_month";
  return "older";
}

/** Human-readable age from conversation start. */
export function formatConversationAge(createdAt: string, now = new Date()): string {
  const days = conversationAgeDays(createdAt, now);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  return `${days} days ago`;
}

export interface ConversationHistoryGroup {
  period: ConversationHistoryPeriod;
  label: string;
  conversations: KaiConversation[];
}

export function groupConversationsByPeriod(
  conversations: KaiConversation[],
  now = new Date(),
): ConversationHistoryGroup[] {
  const buckets = new Map<ConversationHistoryPeriod, KaiConversation[]>();

  for (const conversation of conversations) {
    const startDate = conversationStartDate(conversation);
    const period = conversationPeriod(startDate, now);
    const list = buckets.get(period);
    if (list) list.push(conversation);
    else buckets.set(period, [conversation]);
  }

  return CONVERSATION_HISTORY_PERIOD_ORDER.flatMap((period) => {
    const items = buckets.get(period);
    if (!items?.length) return [];
    return [
      {
        period,
        label: CONVERSATION_HISTORY_PERIOD_LABELS[period],
        conversations: items,
      },
    ];
  });
}
